import { Categories } from '@cookspace-shared-contracts/core';

import { baseApi } from '@/api/baseApi';
import { parseWithSchema } from '@/lib/safeParse';

/**
 * RTK Query endpoints для admin-backend /categories/*.
 *
 * admin-backend живёт на отдельном порту 5002 — префикс /admin
 * не используется. Все ответы парсятся через Zod, чтобы
 * поймать drift контрактов.
 */
export const categoriesApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listCategories: build.query<
            Categories.GetCategoriesResponse,
            Categories.GetCategoriesRequest | void
        >({
            query: (params) => {
                const search: Record<string, string> = {};
                if (params?.type) {
                    search.type = params.type;
                }
                if (params?.isActive !== undefined) {
                    search.isActive = params.isActive ? 'true' : 'false';
                }
                return {
                    url: '/categories',
                    method: 'GET',
                    params: search
                };
            },
            transformResponse: (raw: unknown): Categories.GetCategoriesResponse =>
                parseWithSchema(Categories.GetCategoriesResponseSchema, raw),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map((c) => ({
                            type: 'Category' as const,
                            id: c.id
                        })),
                        { type: 'Category' as const, id: 'LIST' }
                    ]
                    : [{ type: 'Category' as const, id: 'LIST' }]
        }),

        createCategory: build.mutation<
            Categories.CreateCategoryResponse,
            Categories.CreateCategoryRequest
        >({
            query: (body) => ({
                url: '/categories',
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): Categories.CreateCategoryResponse =>
                parseWithSchema(Categories.CreateCategoryResponseSchema, raw),
            invalidatesTags: [{ type: 'Category', id: 'LIST' }]
        }),

        updateCategory: build.mutation<
            Categories.UpdateCategoryResponse,
            { id: string; body: Categories.UpdateCategoryRequest }
        >({
            query: ({ id, body }) => ({
                url: `/categories/${id}`,
                method: 'PATCH',
                body
            }),
            transformResponse: (raw: unknown): Categories.UpdateCategoryResponse =>
                parseWithSchema(Categories.UpdateCategoryResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Category', id: arg.id },
                { type: 'Category', id: 'LIST' }
            ]
        }),

        deleteCategory: build.mutation<Categories.DeleteCategoryResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/categories/${id}`,
                method: 'DELETE'
            }),
            transformResponse: (raw: unknown): Categories.DeleteCategoryResponse =>
                parseWithSchema(Categories.DeleteCategoryResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Category', id: arg.id },
                { type: 'Category', id: 'LIST' }
            ]
        }),

        reorderCategories: build.mutation<
            Categories.ReorderCategoriesResponse,
            Categories.ReorderCategoriesRequest
        >({
            query: (body) => ({
                url: '/categories/reorder',
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): Categories.ReorderCategoriesResponse =>
                parseWithSchema(Categories.ReorderCategoriesResponseSchema, raw),
            invalidatesTags: [{ type: 'Category', id: 'LIST' }]
        })
    })
});

export const {
    useListCategoriesQuery,
    useCreateCategoryMutation,
    useUpdateCategoryMutation,
    useDeleteCategoryMutation,
    useReorderCategoriesMutation
} = categoriesApi;
